import { useParams, useNavigate } from 'react-router-dom';
import { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import { useSectionSpy } from '@/hooks/useSectionSpy';
import { useOnboardingStatus } from '@/hooks/useOnboarding';
import EvalResultsGuideOverlay from '@/components/overlay/EvalResultsGuideOverlay';
import { useAdaptiveGuide } from '@/contexts/AdaptiveGuideContext';
import type { EvalSummary } from '@/data/evaluationGuideContent';
import { GUIDE_DISMISSED_EVAL_RESULTS_KEY } from '@/utils/constants';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  SkeletonText,
  CollapsibleSection,
} from '@/components/ui';
import {
  ErrorHeatMap,
  EnhancedEvaluationDisplay,
  SpeedTempoSection,
  // EvaluationHero is no longer rendered on this page — Slice 1a replaced it
  // with EvalResultsHero. Keeping the import would cause unused-variable lint
  // warnings. The component itself remains in the codebase (used in other
  // contexts and kept for backward compat during the slice-by-slice rollout).
  // CoachingNarrativeCard is also no longer used directly — EvalEditorialCoaching
  // (Slice 1d) replaced its presentation. Both components remain in the codebase.
} from '@/components/evaluation';
// Slice 1a — redesigned hero + theme shell
import EvalResultsHero from '@/components/evaluation/EvalResultsHero';
import GoalScoreSection from '@/components/evaluation/GoalScoreSection';
import GoalScorePublishConsent from '@/components/evaluation/GoalScorePublishConsent';
import {
  CoverageLine,
  WithheldCheckpoints,
} from '@/components/evaluation/CheckpointCoverage';
import EvalResultsPageShell from '@/components/evaluation/EvalResultsPageShell';
// Slice 1d — editorial coaching layout (replaces CoachingNarrativeCard presentation)
import EvalEditorialCoaching from '@/components/evaluation/EvalEditorialCoaching';
// Slice 1b — compact phase strip (one thin segment per phase, click-to-seek)
import EvalPhaseStrip from '@/components/evaluation/EvalPhaseStrip';
// Slice 1e — Drill-In Ledger (demotes diagnostic sections to default-closed drawers)
import EvalLedger from '@/components/evaluation/EvalLedger';
import type { EnhancedEvaluationDisplayRef } from '@/components/evaluation/EnhancedEvaluationDisplay';
import { templateWindowSecondsFromFrames } from '@/components/evaluation/templateWindowHelpers';
import type { PhaseScore, RegionHull, LandmarkDeviation, EconomyPhaseData } from '@/types/api';
import { useEvaluationResult, useFetchPoseData, extractLandmarksFromFrame, mirrorLandmarks } from '@/hooks';
import { useFetchPoseData3D, useDisplayPose3D } from '@/hooks/usePoseExtraction';
import { mirrorH36MJoints, mapComponentDetailsToBoneScores } from '@/utils/skeleton3d';
import { formatDate } from '@/utils/formatters';
import { deriveLeadSides } from '@/utils/leadSideLabels';
import { ENABLE_3D_VISUALIZATION } from '@/utils/constants';
import { useVideoSource } from '@/hooks/useVideoSource';
import CommentsSection from '@/components/comments/CommentsSection';
import { saveEvaluationVideo, getEvaluationHulls } from '@/services/evaluationApi';
import { useAuth } from '@/contexts/AuthContext';
import ShareEvaluationModal from '@/components/evaluation/ShareEvaluationModal';
import ZeroClassBanner from '@/components/evaluation/ZeroClassBanner';
// B-2026-0075: milestone celebration toast
import { MilestoneCelebrationToast } from '@/components/milestones/MilestoneCelebrationToast';
import {
  PlayCircleIcon,
  ExclamationTriangleIcon,
  VideoCameraIcon,
  CloudArrowUpIcon,
  BoltIcon,
  ClockIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// B-2026-0072: Score card image generator (client-side Canvas, no server storage)
// ---------------------------------------------------------------------------

interface ScoreCardProps {
  score: number;
  templateName: string;
  channelName?: string;
  athleteName?: string;
}

function downloadScoreCard({ score, templateName, channelName, athleteName }: ScoreCardProps): void {
  const W = 600;
  const H = 340;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Background
  ctx.fillStyle = '#0B0F14';
  ctx.fillRect(0, 0, W, H);

  // Subtle grid texture (horizontal lines)
  ctx.strokeStyle = 'rgba(255,255,255,0.04)';
  ctx.lineWidth = 1;
  for (let y = 0; y < H; y += 20) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
  }

  // Left accent bar
  const accentColor = score >= 85 ? '#1ED760' : score >= 70 ? '#F5C542' : '#EF4444';
  ctx.fillStyle = accentColor;
  ctx.fillRect(0, 0, 5, H);

  // Score (huge)
  ctx.textAlign = 'left';
  ctx.fillStyle = accentColor;
  ctx.font = `bold 100px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
  const scoreText = Math.round(score).toString();
  ctx.fillText(scoreText, 40, 160);

  // /100
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.font = `bold 28px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
  const scoreWidth = ctx.measureText(scoreText).width;
  ctx.fillText('/100', 40 + scoreWidth - 20 + 14, 158);

  // Template name
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = `600 20px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
  const maxWidth = W - 80;
  const trimmedTemplate = templateName.length > 42 ? templateName.slice(0, 40) + '…' : templateName;
  ctx.fillText(trimmedTemplate, 40, 210, maxWidth);

  // Channel name (if provided)
  let nextY = 240;
  if (channelName) {
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = `400 15px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(channelName, 40, nextY, maxWidth);
    nextY += 26;
  }

  // Athlete name
  if (athleteName) {
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = `500 14px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
    ctx.fillText(`by ${athleteName}`, 40, nextY, maxWidth);
  }

  // Platform watermark (bottom right)
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.font = `400 12px "SF Pro Display", "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText('Evaluated on PointeTracer', W - 20, H - 18);

  // Download
  const link = document.createElement('a');
  link.download = `score-${Math.round(score)}-${templateName.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 30)}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

// ---------------------------------------------------------------------------
// Score helpers
// ---------------------------------------------------------------------------

function normalizeScore(score: number): number {
  return score > 1 ? score : Math.round(score * 100);
}

function scoreColor(n: number): string {
  if (n >= 80) return 'text-success';
  if (n >= 60) return 'text-warning';
  return 'text-danger';
}

/**
 * Human-readable list of body-region names, lower-cased for mid-sentence use.
 * ["Left Leg"] -> "left leg"; ["Left Leg","Right Leg"] -> "left leg and right leg";
 * ["Left Leg","Right Leg","Torso"] -> "left leg, right leg, and torso".
 */
function formatRegionList(regions: string[]): string {
  const parts = regions.map((r) => r.toLowerCase());
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Human labels for the goal_type values that can appear in `gate_failures`.
 *
 * Source of truth is the backend scoring loop, which appends exactly three
 * values — "spatial", "movement" and "axis". The label for `axis` matches
 * GoalResultsSection's category label so a gate failure and its goal card read
 * the same way.
 *
 * This used to be an inline ternary that handled only "spatial" plus a phantom
 * "body_rotation" (a value the backend never emits) and defaulted everything
 * else to "Movement" — so every axis gate failure, including the common Stance
 * Orientation gate, was badged as a movement failure.
 */
const GATE_FAILURE_LABELS: Record<string, string> = {
  spatial: 'Distance',
  movement: 'Movement',
  axis: 'Axis Alignment',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function EvaluationResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: evaluation, isLoading, coachingTimedOut } = useEvaluationResult(id);
  const { user } = useAuth();

  // PR 1 — Role-gating: coaches and admins see all technical sections; athletes
  // see overall score + coaching language only. Pattern from design doc §2.4.
  const isCoach = user?.role === 'coach' || user?.role === 'admin';

  // Adaptive Guide — open by default on first visit; respect dismiss preference.
  const { setGuideOpen, isGuideOpen, report } = useAdaptiveGuide();

  // B-2026-0086: mount scroll-spy producer once the evaluation has loaded.
  // Called unconditionally (Rules of Hooks) — the hook itself guards
  // with IntersectionObserver which silently observes 0 elements when
  // the sections haven't rendered yet.
  useSectionSpy(report);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(GUIDE_DISMISSED_EVAL_RESULTS_KEY) === 'true';
      if (!dismissed) setGuideOpen(true);
    } catch {
      setGuideOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref to communicate with video display component for frame seeking
  const videoDisplayRef = useRef<EnhancedEvaluationDisplayRef>(null);

  // Save video state (Phase 4 — premium users only)
  const [isSavingVideo, setIsSavingVideo] = useState(false);
  const [saveVideoStatus, setSaveVideoStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Share-link modal state (B-2025-0019)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Track current frame for heatmap highlighting
  const [currentFrame, setCurrentFrame] = useState(0);

  // EOM card per-phase expand state (false = collapsed)
  const [eomExpandedPhases, setEomExpandedPhases] = useState<Record<string, boolean>>({});

  // B-2026-0078: first-eval celebration banner.
  // - Fetch onboarding status when the evaluation loads.
  // - Show the banner exactly once: only when this eval is the user's first
  //   (onboarding just became complete) and the banner hasn't been shown yet
  //   (localStorage guard keyed to the evaluation ID).
  const CELEBRATION_BANNER_KEY = `sf_celebration_shown_${id}`;
  const { data: onboardingStatus, refetch: refetchOnboarding } = useOnboardingStatus();
  const [showCelebrationBanner, setShowCelebrationBanner] = useState(false);

  useEffect(() => {
    if (!evaluation?.evaluation_id || !onboardingStatus) return;
    // Only show if this eval is the user's first (first_eval_id matches) and
    // onboarding just completed (onboarding_complete is now true).
    const isFirstEval =
      onboardingStatus.first_eval_id === evaluation.evaluation_id ||
      onboardingStatus.first_eval_id === id;
    const alreadyShown = (() => {
      try { return localStorage.getItem(CELEBRATION_BANNER_KEY) === '1'; }
      catch { return false; }
    })();

    if (isFirstEval && onboardingStatus.onboarding_complete && !alreadyShown) {
      setShowCelebrationBanner(true);
      try { localStorage.setItem(CELEBRATION_BANNER_KEY, '1'); } catch { /* noop */ }
    }
  }, [evaluation?.evaluation_id, id, onboardingStatus, CELEBRATION_BANNER_KEY]);

  // Re-fetch onboarding status once this evaluation loads so the banner
  // picks up the freshly-stamped onboarding_completed_at.
  useEffect(() => {
    if (evaluation?.evaluation_id) {
      refetchOnboarding();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation?.evaluation_id]);

  // Lazy-fetch hull data separately from the main evaluation payload.
  // Hull data was stripped from the main response to reduce payload size by 70-75%.
  // Fetched after the main evaluation loads so scores and coaching render immediately.
  // Non-fatal: if the fetch fails the heatmap overlay simply won't show.
  const [hullData, setHullData] = useState<{
    athlete_region_hulls: RegionHull[];
    template_region_hulls: RegionHull[];
    landmark_deviations: LandmarkDeviation[] | null;
  } | null>(null);
  // True while hull data is actively fetching (shows pulsing placeholder in overlay region).
  const [hullLoading, setHullLoading] = useState(false);

  useEffect(() => {
    if (evaluation?.evaluation_id) {
      setHullLoading(true);
      getEvaluationHulls(evaluation.evaluation_id)
        .then((data) => {
          setHullData(data);
          setHullLoading(false);
        })
        .catch(() => {
          setHullData(null);
          setHullLoading(false);
        });
    }
  }, [evaluation?.evaluation_id]);

  // Warn once per evaluation load when global_body_score is absent from metadata.
  // Moved from the component body into useEffect so it fires exactly once per
  // evaluation_id rather than on every re-render (frame changes, hull loads, etc.).
  //
  // After PR #32 (LFT-063 sentinel fix), global_body_score is intentionally absent
  // for goal-only templates (no body-region annotations). The backend sets
  // goal_only_template_auto_route=true in metadata for these evaluations.
  // Only warn when the field is missing AND the template has body regions — i.e.
  // the field should have been populated but wasn't.
  useEffect(() => {
    const isGoalOnly = evaluation?.metadata?.goal_only_template_auto_route === true;
    if (
      evaluation?.evaluation_id &&
      evaluation.metadata?.global_body_score === undefined &&
      !isGoalOnly
    ) {
      console.warn('[EvaluationResults] global_body_score absent from metadata — backend may not be sending it');
    }
  }, [evaluation?.evaluation_id, evaluation?.metadata?.global_body_score, evaluation?.metadata?.goal_only_template_auto_route]);

  // Resolve video URLs with 4-level fallback (blob → indexeddb → server → none).
  // The hook tries local sources first (Blob URL, IndexedDB) and falls back to
  // a SIGNED server streaming URL (media-auth security sprint, 2026-09-21 —
  // see MEDIA_AUTH_CONTRACT.md). For existing dev videos and "Use Existing"
  // admin flow, the server URL is the primary working source.
  const athleteVideo = useVideoSource(evaluation?.athlete_video_id, 'athlete');
  const templateVideo = useVideoSource(evaluation?.template_video_id, 'template');

  // IMPORTANT: do NOT fall back to the unsigned getVideoUrl() helper here —
  // /videos/{id}/stream 401s without a bearer header or a valid sig/exp pair,
  // and useVideoSource's 'server' level already resolves to a signed URL (or
  // null while the token is loading). templateVideoUrl defaults to '' (not
  // null) only to satisfy EnhancedEvaluationDisplay's required `string` prop.
  const athleteVideoUrl = athleteVideo.url;
  const templateVideoUrl = templateVideo.url ?? '';

  // Fetch pose data for visualization overlays
  const { data: templatePoseData, isLoading: isPoseTemplateLoading } = useFetchPoseData(evaluation?.template_video_id);
  const { data: athletePoseData, isLoading: isPoseAthleteLoading } = useFetchPoseData(evaluation?.athlete_video_id);
  // True while any pose data is still in-flight — passed to EnhancedEvaluationDisplay
  // so it can show a pulsing placeholder instead of silent absence.
  const isPoseDataLoading = isPoseTemplateLoading || isPoseAthleteLoading;

  // Fetch 3D lifted pose data — used for scoring pipeline, retained unchanged.
  const { data: templatePoseData3D } = useFetchPoseData3D(evaluation?.template_video_id);
  const { data: athletePoseData3D } = useFetchPoseData3D(evaluation?.athlete_video_id);

  // Fetch anthropometric display-quality 3D data for the viewer.
  const { data: templateDisplay3D } = useDisplayPose3D(evaluation?.template_video_id);
  const { data: athleteDisplay3D } = useDisplayPose3D(evaluation?.athlete_video_id);

  // Map athlete frame → corresponding template frame via linear interpolation.
  const templateFrameFor2D = useMemo(() => {
    if (!templatePoseData?.length || !athletePoseData?.length) return currentFrame;
    const ratio = currentFrame / Math.max(athletePoseData.length - 1, 1);
    return Math.round(ratio * (templatePoseData.length - 1));
  }, [currentFrame, templatePoseData?.length, athletePoseData?.length]);

  const templateFrameFor3D = useMemo(() => {
    const templateLen = templateDisplay3D?.joints_3d?.length
      ?? templatePoseData3D?.landmarks_3d?.length;
    const athleteLen = athleteDisplay3D?.joints_3d?.length
      ?? athletePoseData3D?.landmarks_3d?.length;
    if (!templateLen || !athleteLen) return currentFrame;
    const ratio = currentFrame / Math.max(athleteLen - 1, 1);
    return Math.round(ratio * (templateLen - 1));
  }, [
    currentFrame,
    templateDisplay3D?.joints_3d?.length,
    athleteDisplay3D?.joints_3d?.length,
    templatePoseData3D?.landmarks_3d?.length,
    athletePoseData3D?.landmarks_3d?.length,
  ]);

  // Extract current frame landmarks for overlays.
  //
  // BUG FIX (2026-08-01, owner report): templateLandmarks was pre-mirrored here
  // whenever the eval mirrored the athlete — correct for the GHOST drawn on the
  // athlete video, but the TEMPLATE panel's own PoseOverlay consumed the same
  // array while assuming "the template is never mirrored", so on mirrored evals
  // (left-handed athlete) the template skeleton rendered X-flipped — to the LEFT
  // of the reference performer. Display cousin of the LFT-021 double-swap family:
  // one consumer needs the mirrored frame, the other the raw frame — so we now
  // export BOTH and each panel takes the one matching its coordinate space.
  //
  // templateLandmarks      → RAW, template panel skeleton (reference frame).
  // templateLandmarksGhost → mirrored iff evaluation.mirrored, for the ghost
  //                          overlaid on the athlete video (athlete's frame).
  const templateLandmarks = useMemo(
    () => extractLandmarksFromFrame(templatePoseData, templateFrameFor2D),
    [templatePoseData, templateFrameFor2D]
  );

  const templateLandmarksGhost = useMemo(() => {
    if (evaluation?.mirrored && templateLandmarks) {
      return mirrorLandmarks(templateLandmarks);
    }
    return templateLandmarks;
  }, [templateLandmarks, evaluation?.mirrored]);

  const athleteLandmarks = useMemo(
    () => extractLandmarksFromFrame(athletePoseData, currentFrame),
    [athletePoseData, currentFrame]
  );

  // Extract per-frame 3D joints for the viewer.
  const templateJoints3D = useMemo(() => {
    if (!ENABLE_3D_VISUALIZATION) return null;
    if (templateDisplay3D?.joints_3d?.length) {
      const frame = Math.min(templateFrameFor3D, templateDisplay3D.joints_3d.length - 1);
      if (frame < 0) return null;
      return templateDisplay3D.joints_3d[frame];
    }
    if (!templatePoseData3D?.landmarks_3d) return null;
    const frame = Math.min(templateFrameFor3D, templatePoseData3D.landmarks_3d.length - 1);
    if (frame < 0) return null;
    return templatePoseData3D.landmarks_3d[frame];
  }, [templateDisplay3D, templatePoseData3D, templateFrameFor3D]);

  const athleteJoints3D = useMemo(() => {
    if (!ENABLE_3D_VISUALIZATION) return null;
    if (athleteDisplay3D?.joints_3d?.length) {
      const frame = Math.min(currentFrame, athleteDisplay3D.joints_3d.length - 1);
      if (frame < 0) return null;
      const joints = athleteDisplay3D.joints_3d[frame];
      return evaluation?.mirrored ? mirrorH36MJoints(joints) : joints;
    }
    if (!athletePoseData3D?.landmarks_3d) return null;
    const frame = Math.min(currentFrame, athletePoseData3D.landmarks_3d.length - 1);
    if (frame < 0) return null;
    const joints = athletePoseData3D.landmarks_3d[frame];
    return evaluation?.mirrored ? mirrorH36MJoints(joints) : joints;
  }, [athleteDisplay3D, athletePoseData3D, currentFrame, evaluation?.mirrored]);

  // True only when both videos have 3D data.
  const has3DData = ENABLE_3D_VISUALIZATION && !!(
    (templateDisplay3D?.joints_3d?.length || templatePoseData3D?.landmarks_3d) &&
    (athleteDisplay3D?.joints_3d?.length || athletePoseData3D?.landmarks_3d)
  );

  const isGravityAligned = templateDisplay3D?.joints_3d?.length
    ? true
    : (templatePoseData3D?.is_gravity_aligned ?? false);

  // Enrich phase_scores with frame information from phase_mapping.
  // Match each phase score to its pair by phase IDENTITY (id/label), not by
  // array index — phase_scores may be a subset of phase_pairs when the backend
  // hides de-prioritized phases (Low importance / weight 0), so index alignment
  // is not safe for a non-trailing de-prioritized phase.
  const enrichedPhaseScores = useMemo(() => {
    const pairs = evaluation?.phase_mapping?.phase_pairs;
    if (!evaluation?.phase_scores || !pairs) {
      return evaluation?.phase_scores || [];
    }
    return evaluation.phase_scores.map((phaseScore, index) => {
      const phasePair =
        pairs.find((pp) => {
          const byId =
            phaseScore.phase_id != null &&
            (pp.athlete_phase?.phase_id === phaseScore.phase_id ||
              pp.template_phase?.phase_id === phaseScore.phase_id);
          const label = phaseScore.phase_name ?? phaseScore.phase_label;
          const byLabel =
            label != null &&
            (pp.template_phase?.label === label ||
              pp.athlete_phase?.label === label);
          return byId || byLabel;
        }) ?? pairs[index]; // legacy fallback when no identity fields are present
      if (phasePair?.athlete_phase) {
        return {
          ...phaseScore,
          start_frame: phasePair.athlete_phase.start_frame,
          end_frame: phasePair.athlete_phase.end_frame,
          duration_frames: phasePair.athlete_phase.duration_frames,
          phase_id: phasePair.athlete_phase.phase_id,
        };
      }
      return phaseScore;
    });
  }, [evaluation]);

  // Derive per-bone scores for the 3D viewer.
  const boneScores3D = useMemo(() => {
    if (!ENABLE_3D_VISUALIZATION) return undefined;
    if (!enrichedPhaseScores) return undefined;
    const phase = enrichedPhaseScores.find(
      (p) =>
        p.start_frame !== undefined &&
        p.end_frame !== undefined &&
        currentFrame >= (p.start_frame ?? 0) &&
        currentFrame <= (p.end_frame ?? Infinity)
    );
    if (!phase?.component_details) return undefined;
    return mapComponentDetailsToBoneScores(phase.component_details);
  }, [enrichedPhaseScores, currentFrame]);

  // Handler for when user clicks on error heatmap
  const handleFrameSeek = useCallback((frameIndex: number) => {
    if (videoDisplayRef.current?.seekToFrame) {
      videoDisplayRef.current.seekToFrame(frameIndex);
    }
    setCurrentFrame(frameIndex);
  }, []);

  // Handler for when user clicks a timestamp chip on a coaching tip.
  // source_frame_pct is 0-100; convert to an absolute frame index using
  // total_frames from evaluation metadata.
  const handleSeekToFramePct = useCallback((pct: number) => {
    const totalFrames = evaluation?.metadata?.total_frames ?? evaluation?.frame_scores?.length ?? 0;
    if (totalFrames === 0) return;
    const frameIndex = Math.round((pct / 100) * totalFrames);
    if (videoDisplayRef.current?.seekToFrame) {
      videoDisplayRef.current.seekToFrame(frameIndex);
    }
    setCurrentFrame(frameIndex);
  }, [evaluation?.metadata?.total_frames, evaluation?.frame_scores?.length]);

  // Template trim window (TVT-2). The reference video is stored in full but only
  // the [trim_start, trim_end] frame range is scored. Convert the backend's frame
  // window (full-video frame space) + template fps into seconds so the results
  // player can clamp the reference <video> to the scored range. For full-video
  // templates the backend sends trim_end_frame=null → endSec=null and startSec=0,
  // which the display treats as "no clamp" (unchanged playback).
  const { templateWindowStartSec, templateWindowEndSec } = useMemo(() => {
    const meta = evaluation?.metadata;
    const { startSec, endSec } = templateWindowSecondsFromFrames(
      meta?.template_trim_start_frame as number | undefined,
      meta?.template_trim_end_frame as number | null | undefined,
      meta?.template_fps as number | undefined,
    );
    return { templateWindowStartSec: startSec, templateWindowEndSec: endSec };
  }, [evaluation?.metadata]);

  // Handler for when user clicks on a phase.
  // B-2026-0086: also reports activePhase to the overlay so a phase tap
  // immediately updates guide content (active tap signal > scroll-spy).
  const handlePhaseSeek = useCallback((phase: PhaseScore) => {
    const startFrame = phase.start_frame ?? 0;
    if (videoDisplayRef.current?.pause) {
      videoDisplayRef.current.pause();
    }
    if (videoDisplayRef.current?.seekToFrame) {
      videoDisplayRef.current.seekToFrame(startFrame);
    }
    setCurrentFrame(startFrame);
    // Signal the guide overlay that a phase has been selected.
    report({ activePhase: phase.phase_index ?? null });
  }, [report]);

  // Save athlete video permanently on the server (paid users only)
  const handleSaveVideo = useCallback(async () => {
    if (!id) return;
    setIsSavingVideo(true);
    setSaveVideoStatus('idle');
    try {
      await saveEvaluationVideo(id);
      setSaveVideoStatus('success');
    } catch {
      setSaveVideoStatus('error');
    } finally {
      setIsSavingVideo(false);
    }
  }, [id]);

  // Focus-phase pill (prototype hero pill #3): the LOWEST-scoring phase's NAME.
  // Name only — no numbers — so it is athlete-safe (owner ruling Q1). Requires
  // ≥2 phases (with one phase, "focus phase" is meaningless).
  const focusPhaseLabel = useMemo(() => {
    if (!enrichedPhaseScores || enrichedPhaseScores.length < 2) return null;
    let worst: { name: string; score: number } | null = null;
    for (const p of enrichedPhaseScores) {
      const raw = p.form_score ?? p.spatial_score ?? p.score ?? p.overall_phase_score;
      if (raw == null) continue;
      const s = raw > 1 ? raw : raw * 100;
      const name = p.phase_name ?? p.phase_label;
      if (!name) continue;
      if (!worst || s < worst.score) worst = { name, score: s };
    }
    return worst ? worst.name : null;
  }, [enrichedPhaseScores]);

  // HOOK-ORDER SAFETY: every hook above this line — the early returns below
  // (isLoading / !evaluation) must never sit above a hook (caught live
  // 2026-08-01: "Rendered more hooks than during the previous render").

  if (isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading evaluation results">
        {/* Page header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>

        {/* Hero score ring + coaching headline skeleton */}
        <div className="rounded-xl bg-dark-elevated-1 border border-border-subtle p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Score ring placeholder — matches EvaluationHero ring size (~120px) */}
            <Skeleton className="h-28 w-28 rounded-full flex-shrink-0" />
            <div className="flex-1 w-full space-y-3">
              <Skeleton className="h-6 w-40" />
              <SkeletonText lines={2} />
              <div className="flex gap-3 pt-1">
                <Skeleton className="h-8 w-24 rounded-lg" />
                <Skeleton className="h-8 w-24 rounded-lg" />
                <Skeleton className="h-8 w-24 rounded-lg" />
              </div>
            </div>
          </div>
        </div>

        {/* EOM + Body Form side-by-side card skeletons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="rounded-xl bg-dark-elevated-1 border border-border-subtle p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-5 w-36 rounded" />
                </div>
                <Skeleton className="h-7 w-10 rounded" />
              </div>
              <SkeletonText lines={2} />
              <div className="space-y-2 pt-1 border-t border-border-subtle">
                {[0, 1, 2].map((j) => (
                  <div key={j} className="flex items-center justify-between">
                    <Skeleton className="h-3 w-24 rounded" />
                    <Skeleton className="h-3 w-10 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Video card placeholder — fixed height so page doesn't jump on load */}
        <div className="rounded-xl bg-dark-elevated-1 border border-border-subtle overflow-hidden">
          <div className="p-4 border-b border-border-subtle flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32 rounded" />
          </div>
          {/* aspect-video placeholder (16:9 = 56.25%) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
            <Skeleton className="aspect-video rounded-lg w-full" />
            <Skeleton className="aspect-video rounded-lg w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="text-center py-12 px-4">
        <h2 className="text-lg font-semibold mb-2">Evaluation not found</h2>
        <p className="text-text-secondary mb-6">
          This evaluation may have been deleted, or you may not have permission to view it.
        </p>
        <div className="flex justify-center gap-3">
          <Button onClick={() => navigate('/evaluations')}>
            Back to evaluations
          </Button>
          <Button variant="secondary" onClick={() => navigate('/')}>
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }

  // ── Lead-side derivation (Req 1) ────────────────────────────────────────────
  // Derives templateLeadSide and athleteLeadSide from evaluation.metadata.mirroring_decision
  // for use in the Trace Pointes functional chip labels and smart defaults.
  // Each panel in EnhancedEvaluationDisplay uses its own lead side independently.
  const { templateLeadSide, athleteLeadSide } = deriveLeadSides(
    evaluation.metadata?.mirroring_decision as Parameters<typeof deriveLeadSides>[0] ?? null
  );

  // Calculate metrics
  const phaseCount = enrichedPhaseScores?.length || 0;
  const regionCount = evaluation.region_scores?.length || 0;


  const hasComponentDetails = enrichedPhaseScores.some(
    (p) => p.component_details && p.component_details.length > 0
  );

  const showFormBreakdown = hasComponentDetails || (evaluation.region_scores && evaluation.region_scores.length > 0);
  const formBreakdownBadge = hasComponentDetails
    ? enrichedPhaseScores.reduce((acc, p) => acc + (p.component_details?.length ?? 0), 0)
    : regionCount;

  const hasNarrative = !!(evaluation.llm_explanation);
  // PS-0a (score-fast split): when llm_explanation is null, the coaching
  // narrative is still generating. Show the loading skeleton rather than
  // hiding the section. Stop once timed out (coachingTimedOut).
  const isCoachingLoading = !hasNarrative && !coachingTimedOut;
  // Show the coaching section whenever there's a narrative, it's still
  // loading, or it timed out (so we can show "unavailable" rather than nothing).
  const showCoachingSection = hasNarrative || isCoachingLoading || coachingTimedOut;

  // ── KPI values (integers) ──────────────────────────────────────────────────
  const overallNorm = normalizeScore(evaluation.overall_score ?? 0);

  // ---------------------------------------------------------------------------
  // Phase-importance-weighted aggregate helper.
  // phase_weight is populated by the phase-weighted scoring pipeline.
  // When absent (legacy evals), defaults to 1.0 so weighted avg == simple avg.
  // ---------------------------------------------------------------------------
  function phaseWeightedAvg(
    phases: typeof enrichedPhaseScores,
    getValue: (p: typeof enrichedPhaseScores[0]) => number | null | undefined
  ): number | null {
    const scored = phases.filter((p) => getValue(p) != null);
    if (scored.length === 0) return null;
    const totalWeight = scored.reduce((sum, p) => sum + (p.phase_weight ?? 1), 0);
    if (totalWeight <= 0) return null;
    const weightedSum = scored.reduce(
      (sum, p) => sum + (getValue(p) as number) * (p.phase_weight ?? 1),
      0
    );
    return weightedSum / totalWeight;
  }

  // Form score: prefer phase_scores aggregate (body_form_score or form_score).
  // Uses phase-importance-weighted average so Low-weight phases contribute less.
  // overall_procrustes_score is a legacy GBM field that is None in the
  // phase-weighted pipeline. Computing from phases avoids the null→0 footgun
  // where null !== undefined but normalizeScore(null) produces 0.
  const formNorm = (() => {
    const phasesWithForm = enrichedPhaseScores.filter(
      (p) => (p.form_score ?? p.spatial_score) != null
    );
    if (phasesWithForm.length > 0) {
      const wavg = phaseWeightedAvg(phasesWithForm, (p) => p.form_score ?? p.spatial_score);
      return wavg != null ? normalizeScore(wavg) : null;
    }
    // Fall back to the legacy top-level field only when it is a real number.
    const legacy = evaluation.overall_procrustes_score;
    return (legacy != null && typeof legacy === 'number') ? normalizeScore(legacy) : null;
  })();

  // Note: warning for absent global_body_score is emitted once-per-eval via useEffect above.

  // EOM: derive a single headline number from economy_of_motion.
  // Backend stores the per-phase aggregate as `phase_score` (0-100 scale).
  // LFT-066 (2026-05-01): EOM is now computed unconditionally for all templates
  // on the full 13-landmark body set. `economy_of_motion` will be absent only
  // when every active phase was below the dynamism threshold or too short.
  const eomPhases = evaluation.metadata?.economy_of_motion;
  let eomNorm: number | null = null;
  if (eomPhases && Object.keys(eomPhases).length > 0) {
    // `phase_score` is the canonical field emitted by the backend (0-100).
    const vals = Object.values(eomPhases)
      .map((p) => p?.phase_score ?? null)
      .filter((v): v is number => v != null);
    if (vals.length > 0) {
      // phase_score is already 0-100 — no multiplication needed.
      eomNorm = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    } else {
      console.warn('[EvaluationResults] economy_of_motion present but phase_score field not found in phase entries');
    }
  } else if (!eomPhases) {
    console.warn('[EvaluationResults] economy_of_motion absent from metadata — all phases may have been static or too short');
  }

  // B-2026-0086: Build a minimal eval summary for dynamic guide body resolvers.
  // Constructed here, below the data guard, so evaluation is guaranteed non-null.
  const evalSummary: EvalSummary = {
    overallScore: overallNorm,
    phases: enrichedPhaseScores.map((p) => ({
      phase_label: p.phase_label,
      phase_name: p.phase_name,
      form_score: p.form_score,
      spatial_score: p.spatial_score,
      // phase_weight can be null from PhaseScore — coerce to undefined for EvalSummary
      phase_weight: p.phase_weight ?? undefined,
    })),
    hasGoals: !!(
      evaluation.spatial_goal_results &&
      Object.keys(evaluation.spatial_goal_results).length > 0
    ),
    hasGemini: !!(
      evaluation.metadata?.gemini_observations &&
      evaluation.metadata.gemini_observations.length > 0
    ),
    mirrored: evaluation.mirrored,
    automaticFail: evaluation.automatic_fail,
  };

  // B-2026-0075: Extract newly_awarded_milestones from metadata.
  // This field is NON-SCORING — never used in any scoring logic; only consumed
  // here to show a one-time celebration toast.
  const newlyAwardedMilestones: string[] =
    (evaluation?.metadata?.newly_awarded_milestones as string[] | undefined) ?? [];

  return (
    <EvalResultsPageShell>
    {/*
     * Landscape Focus Mode (max-lg:landscape:)
     *
     * In landscape orientation on small screens (phones — lg breakpoint excluded so
     * tablets and desktops are completely unchanged):
     *
     *  - The page container becomes a full-height, no-scroll, no-gap flex column.
     *  - Every section EXCEPT the video section is hidden via max-lg:landscape:hidden.
     *  - The video section fills the remaining viewport height.
     *  - A slim score chip is overlaid at the top-right of the video section.
     *
     * Desktop (lg+), portrait (all), and tablet-landscape (lg+) are BYTE-IDENTICAL
     * — every Tailwind rule here is scoped behind max-lg:landscape:.
     *
     * data-testid="landscape-focus-container" is used by the regression test to assert
     * the hide classes are present.
     */}
    <div
      className="space-y-6 max-lg:landscape:space-y-0 max-lg:landscape:h-[100dvh] max-lg:landscape:overflow-hidden max-lg:landscape:flex max-lg:landscape:flex-col"
      data-testid="landscape-focus-container"
    >
      {/* B-2026-0075: Milestone celebration toast — one-time; guard in localStorage.
          Portal-based: no visual layout impact regardless of landscape mode. */}
      <MilestoneCelebrationToast
        evaluationId={evaluation?.evaluation_id}
        milestoneIds={newlyAwardedMilestones}
      />

      {/* B-2026-0078: First-eval celebration banner — hidden in landscape focus mode.
          The landscape:hidden class is on a WRAPPER div, not the banner div itself,
          because the banner has inline style={{ display: 'flex' }} which would
          override a CSS class-based display:none at equal specificity. A parent
          wrapper avoids that conflict. */}
      {showCelebrationBanner && (
        <div className="max-lg:landscape:hidden">
        <div
          data-testid="first-eval-celebration-banner"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            backgroundColor: 'rgba(30, 215, 96, 0.07)',
            border: '1px solid rgba(30, 215, 96, 0.28)',
            borderRadius: '14px',
            padding: '16px 20px',
            position: 'relative',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: 'rgba(30, 215, 96, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SparklesIcon className="h-5 w-5" style={{ color: 'var(--color-accent)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '14px',
                color: 'var(--color-text-primary)',
                margin: '0 0 4px',
              }}
            >
              You completed your first evaluation!
            </p>
            <p
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
                margin: '0 0 10px',
                lineHeight: 1.5,
              }}
            >
              {evaluation.created_by_display_name
                ? `Your coach ${evaluation.created_by_display_name} will review your results.`
                : 'Your coach will review your results.'}
              {' '}Your progress journey has officially started.
            </p>
            {/* Share CTA — links to the eval page URL for easy copy */}
            <button
              onClick={() => {
                try {
                  navigator.clipboard.writeText(window.location.href);
                } catch {
                  // fallback silently
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-accent)',
                background: 'none',
                border: '1px solid rgba(30,215,96,0.3)',
                borderRadius: '999px',
                padding: '5px 12px',
                cursor: 'pointer',
                transition: 'background-color 150ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'rgba(30,215,96,0.08)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              }}
            >
              Share your score
            </button>
          </div>
          {/* Dismiss */}
          <button
            onClick={() => setShowCelebrationBanner(false)}
            aria-label="Dismiss celebration banner"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              color: 'var(--color-text-muted)',
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: '16px', lineHeight: 1 }}>×</span>
          </button>
        </div>
        </div>
      )}

      {/* Adaptive Guide overlay — rendered via Portal, pointer-events-none shell.
          Portal renders outside this tree; no landscape class needed here. */}
      <EvalResultsGuideOverlay evalSummary={evalSummary} />

      {/* PageHeader — hidden in landscape focus mode */}
      <PageHeader
        className="max-lg:landscape:hidden"
        title={
          evaluation.metadata?.focal_figure_label
            ? `${evaluation.template_name ?? 'Evaluation Results'} · ${evaluation.metadata.focal_figure_label}`
            : 'Evaluation Results'
        }
        description={`Evaluated on ${formatDate(evaluation.created_at)}${evaluation.created_by_display_name ? ` by ${evaluation.created_by_display_name}` : ''}`}
        showBack
        action={
          !isGuideOpen ? (
            <button
              type="button"
              data-testid="eval-guide-button"
              onClick={() => setGuideOpen(true)}
              aria-label="Open adaptive guide"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
              style={{
                backgroundColor: 'var(--color-surface-raised-2)',
                border: '1px solid color-mix(in srgb, var(--color-accent-cyan) 40%, transparent)',
                color: 'var(--color-accent-cyan)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-surface-raised-3)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-surface-raised-2)';
              }}
            >
              <QuestionMarkCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              Guide
            </button>
          ) : undefined
        }
      />

      {/* Shared-with badge — hidden in landscape focus mode */}
      {evaluation.shared_with_display_name && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-info/10 border border-info/20 text-sm w-fit max-lg:landscape:hidden">
          <UserIcon className="h-4 w-4 text-info flex-shrink-0" />
          <span className="text-text-secondary">
            Shared with{' '}
            <span className="font-medium text-text-primary">
              {evaluation.shared_with_display_name}
            </span>
          </span>
        </div>
      )}

      {/* Focal Person Indicator — hidden in landscape focus mode */}
      {evaluation.metadata?.focal_person_index !== undefined &&
        evaluation.metadata.focal_person_index > 0 &&
        evaluation.metadata.person_count !== undefined &&
        evaluation.metadata.person_count > 1 && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-info/10 border border-info/20 text-sm text-text-secondary max-lg:landscape:hidden">
          <VideoCameraIcon className="h-4 w-4 text-info flex-shrink-0" />
          <span>
            Evaluating{' '}
            <span className="font-medium text-text-primary">
              Person {evaluation.metadata.focal_person_index + 1}
            </span>{' '}
            of {evaluation.metadata.person_count} detected in this video
          </span>
        </div>
      )}

      {/* Partial-body coverage warning (B-2026-0089) — hidden in landscape focus mode */}
      {Array.isArray(evaluation.metadata?.partial_body?.accidental_regions) &&
        evaluation.metadata.partial_body.accidental_regions.length > 0 && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20 text-sm text-text-secondary w-fit max-lg:landscape:hidden"
          data-testid="partial-body-warning"
        >
          <ExclamationTriangleIcon className="h-4 w-4 text-warning flex-shrink-0" />
          <span>
            <span className="font-medium text-text-primary">
              {formatRegionList(evaluation.metadata.partial_body.accidental_regions)}
            </span>{' '}
            {evaluation.metadata.partial_body.accidental_regions.length > 1
              ? 'were'
              : 'was'}{' '}
            not fully visible in this video, so {evaluation.metadata.partial_body.accidental_regions.length > 1 ? 'they' : 'it'} could not be scored. Re-record with the full body in frame for a complete evaluation.
          </span>
        </div>
      )}

      {/* Mirroring Decision Audit Badges — coach/admin only (technical audit trail).
          Hidden in landscape focus mode — these are secondary diagnostic info. */}
      {isCoach && evaluation.metadata?.mirroring_decision?.source === 'layer1_declared' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-sm w-fit max-lg:landscape:hidden">
          <span className="text-success font-medium">Handedness declared</span>
          <span className="text-text-secondary">— reliable scoring</span>
        </div>
      )}
      {isCoach && evaluation.metadata?.mirroring_decision?.source === 'layer1_6_template_declared_athlete_detected' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-sm w-fit max-lg:landscape:hidden">
          <span className="text-success font-medium">Template lead side declared</span>
          <span className="text-text-secondary">— athlete side auto-detected; orientation handled automatically</span>
        </div>
      )}
      {isCoach && evaluation.metadata?.mirroring_decision?.source === 'layer2_detection' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20 text-sm w-fit max-lg:landscape:hidden">
          <span className="text-warning font-medium">Handedness inferred from video</span>
          <span className="text-text-secondary">— declare in athlete profile for reliable scoring</span>
        </div>
      )}
      {isCoach && evaluation.metadata?.mirroring_decision?.source === 'rotational_invariant' && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20 text-sm w-fit cursor-help max-lg:landscape:hidden"
          title="This movement is rotational — both athlete orientations were evaluated and the better-matching one was used for scoring."
        >
          <span className="text-success font-medium">Rotational scoring</span>
          <span className="text-text-secondary">— orientation-invariant</span>
        </div>
      )}
      {isCoach && evaluation.metadata?.mirroring_decision?.source === 'layer3_diagnostic' && (() => {
        const delta = evaluation.metadata?.mirroring_decision?.diagnostic_pair_delta;
        const tooltipText = delta != null
          ? `Mirroring would score ${Math.abs(delta).toFixed(1)} points ${delta > 0 ? 'higher' : 'lower'} — verify athlete handedness in profile for certainty.`
          : 'Handedness could not be determined reliably — verify athlete handedness in profile for certainty.';
        return (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20 text-sm w-fit cursor-help max-lg:landscape:hidden"
            title={tooltipText}
          >
            <span className="text-warning font-medium">Handedness ambiguous</span>
            <span className="text-text-secondary">— verify in profile</span>
          </div>
        );
      })()}

      {/* NOTE (Slice 1a): the standalone "Mirrored to your handedness" badge that
          was here has been moved into the EvalResultsHero pills above.  The pill
          is visible to ALL users — athletes and coaches alike — which was the
          original intent of this badge.  data-testid="mirroring-note" now lives
          on the hero pill (data-testid="eval-results-hero-pill-mirror"). */}

      {/* Automatic Fail Banner — hidden in landscape focus mode */}
      {evaluation.automatic_fail && evaluation.gate_failures && evaluation.gate_failures.length > 0 && (
        <div className="bg-red-900/90 border-2 border-red-500 rounded-lg p-5 shadow-lg max-lg:landscape:hidden">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-7 w-7 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-red-100">
                Automatic Fail - Required Conditions Not Met
              </h3>
              <p className="text-red-200 text-sm mt-1 mb-3">
                One or more required goals (gates) were not passed. The evaluation score has been set to 0.
              </p>
              <div className="space-y-2">
                {evaluation.gate_failures.map((failure, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2 bg-red-950/50 rounded-md p-3 border border-red-800"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-red-400 bg-red-900/50 px-2 py-0.5 rounded">
                      {GATE_FAILURE_LABELS[failure.goal_type] ?? failure.goal_type}
                    </span>
                    <div className="flex-1">
                      <p className="text-red-100 font-medium">{failure.annotation_label}</p>
                      <p className="text-red-300 text-sm">{failure.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gates step 2/3 — rotation-boundary ceiling banner (graded, never a zero).
          One verdict, many renderings: this reads the same audit dict the
          decomposition line item and narrative use. */}
      {evaluation.metadata?.boundary_ceiling_applied &&
        (evaluation.metadata.boundary_ceiling_applied.capped ?? true) &&
        evaluation.metadata.boundary_ceiling_applied.score_before_cap >
          evaluation.metadata.boundary_ceiling_applied.cap && (
        <div className="bg-amber-900/80 border-2 border-amber-500 rounded-lg p-5 shadow-lg max-lg:landscape:hidden">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-7 w-7 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-100">
                Orientation Boundary Exceeded — Score Capped
              </h3>
              <p className="text-amber-200 text-sm mt-1">
                The movement's overall orientation sat outside the template's
                boundary (median{' '}
                {evaluation.metadata.boundary_ceiling_applied.profile_median?.toFixed(1)}°
                {' '}vs boundary{' '}
                {evaluation.metadata.boundary_ceiling_applied.boundary_threshold?.toFixed(0)}°,
                derived from the coach's tolerance). The score is capped at{' '}
                {evaluation.metadata.boundary_ceiling_applied.cap.toFixed(0)} so a
                wrong-direction attempt can't read as a mediocre one — it is not
                an automatic fail.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── PR-3: Zero-class banner ─────────────────────────────────────── */}
      {/* Owner Ruling 4 (law): a score of 0.0 from pricing MUST be explained */}
      {/* to the athlete in plain language — never a bare zero.               */}
      {/* Stacking decision: when automatic_fail is also true, the gate-       */}
      {/* failure banner above already names each failed goal; suppress this   */}
      {/* banner to avoid duplicate "score = 0" messaging.                    */}
      {/* All users: no role gate. Owner Ruling 4 applies to athletes above   */}
      {/* all others.                                                          */}
      {/* ZeroClassBanner wrapper — hidden in landscape focus mode */}
      {evaluation && (
        <div className="max-lg:landscape:hidden">
          <ZeroClassBanner
            pricingResult={evaluation.metadata?.pricing_result ?? null}
            suppressIfAutoFail={!!evaluation.automatic_fail}
            overallScore={evaluation.overall_score}
            hasCoachingNarrative={
              !!(evaluation.llm_explanation?.overall_summary)
            }
          />
        </div>
      )}

      {/* ── 1. EvalResultsHero — Slice 1a redesigned hero ─────────────────── */}
      {/* B-2026-0086: data-guide-section drives useSectionSpy scroll spy       */}
      {/* Replaces EvaluationHero (SVG ring) on this page per owner decision D4. */}
      {/* Coach metric tiles (FORM / EOM) are kept BELOW in the existing cards   */}
      {/* until Slice 1e relocates them to the ledger section.                   */}
      {/* Hidden in landscape focus mode — score is shown via the landscape score chip. */}
      <div data-guide-section="hero-overall" className="max-lg:landscape:hidden">
        <EvalResultsHero
          overallScore={evaluation.overall_score ?? 0}
          coachingHeadline={
            evaluation.llm_explanation?.overall_summary
            ?? evaluation.metadata?.eom_narrative?.headline
          }
          phaseCount={phaseCount}
          /* Prototype pill #3: "<Phase> is your focus phase" — the lowest-scoring
             phase, NAME ONLY (athlete-safe per owner ruling Q1: names + colors,
             no numbers). Derived from the same enriched scores the strip uses. */
          focusPhaseLabel={focusPhaseLabel}
          /* Mirror pill: metadata.mirroring_decision is the audit trail (newer
             evals); fall back to the top-level mirrored flag so older evals
             (pre-decision-metadata) still surface the pill (owner fidelity
             review 2026-08-01 — the pill was missing on a mirrored eval). */
          shouldMirror={
            evaluation.metadata?.mirroring_decision?.should_mirror === true
            || evaluation.mirrored === true
          }
          speedAssessment={evaluation.metadata?.speed_assessment ?? null}
        />

        {/* Checkpoint coverage (viewpoint observability Phase 1) — how many of
            the coach's checkpoints this video could actually be scored
            against. Renders nothing when all of them were (the common path)
            and on every evaluation produced before the coverage contract.
            All roles: a withheld check is not a coach-only fact. */}
        <CoverageLine
          coverage={evaluation.metadata?.coverage ?? null}
          className="mt-1"
        />
        <WithheldCheckpoints
          coverage={evaluation.metadata?.coverage ?? null}
          className="mt-3"
        />
      </div>

      {/* ── Goal Score (PR-7a) — Form stays the ring above, Goal/Total get   ───
          their own treatment directly under it. Renders nothing when this
          evaluation carries no goals[] and no total_score (SPORTFORM_GOAL_SCORE
          off, or no eligible Goal on this template) — byte-identical to the
          page before this feature existed. All roles: Goal cards are no more
          coach-only than Check cards are. */}
      <div data-guide-section="goal-score" className="max-lg:landscape:hidden space-y-3">
        <GoalScoreSection
          goalBonusScore={evaluation.goal_bonus_score}
          formEffect={evaluation.form_effect}
          totalScore={evaluation.total_score}
          formScore={evaluation.overall_score ?? 0}
          goals={evaluation.goals}
        />
        {/* Publish-to-leaderboard consent — athlete's own one-line Yes/No,
            directly under the hero/Goal Score block, impossible to scroll
            past (F §3 / REPORT.md §5.6). The component hides itself when the
            backend's own ACL says this viewer has no standing to see it. */}
        <GoalScorePublishConsent
          evaluationId={evaluation.evaluation_id}
          channelName={evaluation.metadata?.channel_name as string | undefined}
        />
      </div>

      {/* HeroScoreSection (old duplicate dashboard) intentionally removed.
          Coach KPIs (FORM / EOM) remain in the cards below (coach-only) until
          Slice 1e relocates them to the ledger section. */}

      {/* ── 2. EvalPhaseStrip — Slice 1b compact phase strip ─────────────────
          One thin colored segment per phase (name + color bar). Visible to ALL
          users: athletes see the color signal; coaches also see numeric scores.
          Click a segment → seeks the video to that phase (handlePhaseSeek).
          The existing Phase Breakdown section (CompactPhaseScores in
          CollapsibleSection) remains below — this strip is additive only.
          Slice 1e will demote the detailed breakdown into a ledger drawer.   */}
      {/* Phase strip — hidden in landscape focus mode */}
      {enrichedPhaseScores && enrichedPhaseScores.length > 0 && (
        <div data-testid="eval-phase-strip-wrapper" className="max-lg:landscape:hidden">
          <EvalPhaseStrip
            phaseScores={enrichedPhaseScores}
            onPhaseSeek={handlePhaseSeek}
            showScore={isCoach}
          />
        </div>
      )}

      {/* ── 3. EOM + GBM explanatory cards (coach/admin only) ───────────────────
          Side-by-side panels explaining what Economy of Motion and Global
          Body Match mean, with per-phase breakdowns where available.
          Hidden from athletes — "bone direction similarity" and "joint angle
          accuracy" are biomechanical terms not meaningful to athletes.       */}
      {/* EOM + GBM cards — hidden in landscape focus mode */}
      {isCoach && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-lg:landscape:hidden">
        {/* Economy of Motion card */}
        <div data-guide-section="eom-card" className="rounded-xl bg-dark-elevated-1 border border-border-subtle p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <BoltIcon className="h-5 w-5 text-accent flex-shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-text-primary">Economy of Motion</h3>
                <p className="text-xs text-text-muted mt-0.5">Already factored into Form Score (15% weight)</p>
              </div>
            </div>
            {eomNorm !== null ? (
              <span className={`text-2xl font-bold tabular-nums ${scoreColor(eomNorm)}`}>
                {eomNorm}
              </span>
            ) : (
              <span className="text-lg font-bold text-text-muted">—</span>
            )}
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">
            How efficiently the athlete moved through each phase. Higher scores mean less wasted
            motion: smoother trajectories, fewer course corrections, less energy spent on movements
            that don't contribute to the goal.
          </p>
          {/* Per-phase breakdown with expandable landmark detail */}
          {evaluation.metadata?.economy_of_motion &&
            Object.keys(evaluation.metadata.economy_of_motion).length > 0 && (
            <div className="space-y-1.5 pt-1 border-t border-border-subtle">
              {Object.entries(evaluation.metadata.economy_of_motion).map(([phase, data]: [string, EconomyPhaseData]) => {
                // `phase_score` is the canonical backend field (0-100 scale).
                const phaseScore = data?.phase_score ?? null;
                const phasePct = phaseScore != null ? Math.round(phaseScore) : null;
                const worstLandmarks: Array<{ name: string; score: number; direction: number; magnitude: number }> =
                  data?.worst_landmarks ?? [];
                const isExpanded = eomExpandedPhases[phase] ?? false;
                const hasDetail = worstLandmarks.length > 0;
                return (
                  <div key={phase}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary capitalize">{phase.replace(/_/g, ' ')}</span>
                      <div className="flex items-center gap-2">
                        {phasePct !== null ? (
                          <span className={`font-medium tabular-nums ${scoreColor(phasePct)}`}>
                            {phasePct}
                          </span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                        {hasDetail && (
                          <button
                            onClick={() =>
                              setEomExpandedPhases((prev) => ({ ...prev, [phase]: !isExpanded }))
                            }
                            className="p-0.5 rounded text-text-muted hover:text-text-secondary transition-colors"
                            aria-label={isExpanded ? 'Collapse landmark detail' : 'Expand landmark detail'}
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDownIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Expandable per-landmark breakdown */}
                    {isExpanded && hasDetail && (
                      <div className="mt-2 space-y-1.5 pl-2 border-l border-border-subtle">
                        <p className="text-xs font-semibold uppercase tracking-wider text-text-muted pb-0.5">
                          Worst landmarks
                        </p>
                        {[...worstLandmarks]
                          .sort((a, b) => a.score - b.score)
                          .map((lm, idx) => {
                            const lmPct = Math.round(lm.score);
                            const dirPct = Math.round((lm.direction ?? 0) * 100);
                            const magPct = Math.round((lm.magnitude ?? 0) * 100);
                            const formattedName = lm.name
                              .replace(/_/g, ' ')
                              .split(' ')
                              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ');
                            return (
                              <div
                                key={`${lm.name}-${idx}`}
                                className="flex items-center justify-between gap-2 py-1"
                              >
                                <div className="min-w-0">
                                  <span className="text-xs text-text-secondary truncate block">
                                    {formattedName}
                                  </span>
                                  <span className="text-[10px] text-text-muted">
                                    Dir: {dirPct}% · Speed: {magPct}%
                                  </span>
                                </div>
                                <span className={`text-xs font-semibold tabular-nums shrink-0 ${scoreColor(lmPct)}`}>
                                  {lmPct}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {eomNorm === null && (
            <p className="text-xs text-text-muted">
              <span className="font-medium">No economy data for this evaluation</span> — all phases
              were either too static or too short for economy analysis.
            </p>
          )}
        </div>

        {/* Global Body Match card */}
        <div data-guide-section="body-form-card" className="rounded-xl bg-dark-elevated-1 border border-border-subtle p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-accent flex-shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-text-primary">Body Form Score</h3>
                <p className="text-xs text-text-muted mt-0.5">Contributes to Form Score (incl. EOM at 15%)</p>
              </div>
            </div>
            {formNorm !== null ? (
              <span className={`text-2xl font-bold tabular-nums ${scoreColor(Math.round(formNorm))}`}>
                {Math.round(formNorm)}
              </span>
            ) : (
              <span className="text-lg font-bold text-text-muted">—</span>
            )}
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">
            How closely the athlete's overall body shape matches the reference at each frame.
            Combines bone direction similarity with joint angle accuracy across the full body.
            Independent of body proportions — works the same for adults and children.
          </p>
          {/* Per-phase breakdown from phase scores */}
          {enrichedPhaseScores && enrichedPhaseScores.length > 0 && (
            <div className="space-y-1.5 pt-1 border-t border-border-subtle">
              {enrichedPhaseScores.map((phase) => {
                const phaseGbm = phase.form_score ?? phase.spatial_score ?? null;
                const phaseGbmPct = phaseGbm != null ? normalizeScore(phaseGbm) : null;
                const label = phase.phase_label ?? phase.phase_name ?? `Phase ${phase.phase_index ?? ''}`;
                return (
                  <div key={phase.phase_id ?? phase.phase_name ?? label} className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary capitalize">
                      {label.replace(/_/g, ' ')}
                    </span>
                    {phaseGbmPct !== null ? (
                      <span className={`font-medium tabular-nums ${scoreColor(phaseGbmPct)}`}>
                        {phaseGbmPct.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {/* Only show "not computed" when there is genuinely no data at all —
              neither a headline form score nor any per-phase form_score values. */}
          {formNorm === null && enrichedPhaseScores.every((p) => (p.form_score ?? p.spatial_score) == null) && (
            <p className="text-xs text-text-muted italic">
              Body Form Score data was not computed for this evaluation.
            </p>
          )}
        </div>
      </div>}

      {/* ── 4. Video Comparison — full width ─────────────────────────────────── */}
      {/* B-2026-0086: section wrapper for scroll-spy */}
      {/* In landscape focus mode this section fills the remaining viewport height.
          max-lg:landscape:flex-1 consumes the flex space inside the container.
          max-lg:landscape:flex + flex-col ensure the Card fills the slot.
          data-testid="landscape-video-section" used by regression tests. */}
      <div
        data-guide-section="video-analysis"
        data-testid="landscape-video-section"
        className="max-lg:landscape:flex-1 max-lg:landscape:overflow-hidden max-lg:landscape:flex max-lg:landscape:flex-col max-lg:landscape:relative"
      >
        {/* ── Landscape score chip — only visible in landscape-phone mode ──
            Floats over the video at top-center. Shows overall score + mirrored badge.
            hidden by default; displayed only at max-lg:landscape via flex.
            data-testid="landscape-score-chip" used by regression tests. */}
        <div
          data-testid="landscape-score-chip"
          className="hidden max-lg:landscape:flex absolute top-2 left-1/2 -translate-x-1/2 z-20 items-center gap-2 px-3 py-1 rounded-full text-sm font-bold pointer-events-none select-none"
          style={{
            background: 'rgba(13, 19, 28, 0.85)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'var(--color-text-primary)',
          }}
          aria-label={`Overall score: ${Math.round(evaluation.overall_score ?? 0)}`}
        >
          <span style={{ color: 'var(--color-text-muted)', fontSize: '11px', fontWeight: 500 }}>
            Score
          </span>
          <span
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color:
                (evaluation.overall_score ?? 0) >= 80
                  ? 'var(--color-signal-good)'
                  : (evaluation.overall_score ?? 0) >= 60
                  ? 'var(--color-signal-warn)'
                  : 'var(--color-signal-bad)',
            }}
          >
            {Math.round(evaluation.overall_score ?? 0)}
          </span>
          {evaluation.metadata?.mirroring_decision?.should_mirror === true && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 500,
                color: 'var(--color-text-muted)',
                borderLeft: '1px solid rgba(255,255,255,0.15)',
                paddingLeft: '6px',
              }}
            >
              mirrored
            </span>
          )}
        </div>

      <Card className="max-lg:landscape:flex-1 max-lg:landscape:overflow-hidden max-lg:landscape:flex max-lg:landscape:flex-col max-lg:landscape:rounded-none max-lg:landscape:border-0">
        <CardHeader className="pb-2 max-lg:landscape:hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <PlayCircleIcon className="h-5 w-5 text-accent" />
              <CardTitle>Video Analysis</CardTitle>
            </div>

            {/* Save Video button — visible to paid users when video is only available locally */}
            {user?.tier === 'paid' &&
              (athleteVideo.sourceType === 'blob' || athleteVideo.sourceType === 'indexeddb') &&
              saveVideoStatus !== 'success' && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveVideo}
                    disabled={isSavingVideo}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#F4C95D]/10 text-[#F4C95D] border border-[#F4C95D]/30 hover:bg-[#F4C95D]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Save this video permanently"
                  >
                    <CloudArrowUpIcon className="h-4 w-4" />
                    {isSavingVideo ? 'Saving...' : 'Save this video'}
                  </button>
                </div>
              )}

            {/* Save video error — shown in the same UI region as the success state
                (Frontend #8 / B-2026-0024). Visible regardless of video source type
                so any failure path is surfaced to the user. */}
            {saveVideoStatus === 'error' && (
              <span
                role="alert"
                className="flex items-center gap-1.5 text-xs text-danger font-medium"
              >
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                Save failed — please try again
              </span>
            )}

            {/* Success confirmation after save */}
            {saveVideoStatus === 'success' && (
              <span className="flex items-center gap-1.5 text-xs text-[#1ED760] font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                Video saved permanently
              </span>
            )}
          </div>
          {(hullData?.athlete_region_hulls ?? evaluation.athlete_region_hulls ?? []).length > 0 && (
            <p className="text-sm text-text-muted mt-1">
              Use the controls below the video to toggle body region overlays and adjust display settings.
            </p>
          )}
        </CardHeader>
        {/* CardContent: in landscape the content fills the card vertically.
            space-y-4 is cleared in landscape so the video display expands.
            max-lg:landscape:p-0 removes card padding that steals height. */}
        <CardContent className="space-y-4 max-lg:landscape:space-y-0 max-lg:landscape:p-0 max-lg:landscape:flex-1 max-lg:landscape:flex max-lg:landscape:flex-col max-lg:landscape:overflow-hidden">
          {/* Performance Timeline — hidden in landscape focus mode (saves vertical space) */}
          {evaluation.frame_scores && evaluation.frame_scores.length > 0 && (
            <div className="bg-dark-elevated-1 rounded-lg p-4 max-lg:landscape:hidden">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-text-primary">Performance Timeline</p>
                <p className="text-xs text-text-muted">Click to seek • Phases clickable below</p>
              </div>
              <ErrorHeatMap
                frameScores={evaluation.frame_scores}
                totalFrames={evaluation.metadata?.total_frames || evaluation.frame_scores.length}
                currentFrame={currentFrame}
                onFrameClick={handleFrameSeek}
                phases={enrichedPhaseScores}
                onPhaseClick={handlePhaseSeek}
                showPhaseLabels={true}
              />
            </div>
          )}

          {/* Hull-loading indicator — pulsing bar shown while overlay data is fetching.
              Renders above the video only when hull data is expected (evaluation
              has region annotations) but hasn't arrived yet. Non-fatal: clears
              automatically when hullData resolves or fails. */}
          {hullLoading && (
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-elevated-1 border border-border-subtle animate-pulse"
              aria-label="Loading overlay data"
              aria-live="polite"
            >
              <Skeleton className="h-3 w-3 rounded-full flex-shrink-0" />
              <span className="text-xs text-text-muted">Loading overlay data...</span>
            </div>
          )}

          {/* Side-by-side video comparison.
              In landscape-phone: flex-1 + overflow-hidden make it fill the remaining
              card height so the videos span the full viewport without scrolling. */}
          {evaluation.template_video_id && evaluation.athlete_video_id ? (
            <EnhancedEvaluationDisplay
              className="max-lg:landscape:flex-1 max-lg:landscape:overflow-hidden"
              ref={videoDisplayRef}
              templateVideoUrl={templateVideoUrl}
              athleteVideoUrl={athleteVideoUrl}
              frameScores={evaluation.frame_scores || []}
              athleteRegionHulls={hullData?.athlete_region_hulls ?? evaluation.athlete_region_hulls ?? []}
              templateRegionHulls={hullData?.template_region_hulls ?? evaluation.template_region_hulls ?? []}
              phaseMapping={evaluation.phase_mapping}
              templateLandmarks={templateLandmarks}
              templateLandmarksGhost={templateLandmarksGhost}
              athleteLandmarks={athleteLandmarks}
              landmarkDeviations={
                // Trailing `?? []` matters: evals WITHOUT per-landmark deviation
                // data store null, and `a ?? b` passes a null RIGHT side through
                // verbatim. Downstream overlays' `= []` parameter defaults only
                // fire on undefined, so a null here reached
                // DifferentialSkeletonOverlay's .filter and silently blanked the
                // athlete Pose skeleton (the draw try/catch ate the TypeError).
                hullData?.landmark_deviations ?? evaluation.landmark_deviations ?? []
              }
              mirrored={evaluation?.mirrored}
              templateLeadSide={templateLeadSide}
              athleteLeadSide={athleteLeadSide}
              spatialGoalResults={evaluation.spatial_goal_results}
              athletePoseData={athletePoseData}
              templatePoseData={templatePoseData}
              isPoseDataLoading={isPoseDataLoading}
              onFrameChange={setCurrentFrame}
              templateJoints3D={templateJoints3D}
              athleteJoints3D={athleteJoints3D}
              boneScores={boneScores3D}
              has3DData={has3DData}
              isGravityAligned={isGravityAligned}
              pose3dDepthReliable={templateDisplay3D?.pose_3d_depth_reliable}
              trailEnabledByDefault={evaluation.metadata?.trail_enabled_by_default}
              templateWindowStartSec={templateWindowStartSec}
              templateWindowEndSec={templateWindowEndSec}
            />
          ) : (
            <div className="p-8 text-center text-text-secondary bg-dark-elevated-1 rounded-lg">
              <p>Video comparison data is not available for this evaluation.</p>
            </div>
          )}

          {/* Color Legend — hidden in landscape focus mode */}
          {(hullData?.athlete_region_hulls ?? evaluation.athlete_region_hulls ?? []).length > 0 && (
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border-subtle max-lg:landscape:hidden">
              <span className="text-xs font-medium text-text-muted">Region Colors:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-success" />
                <span className="text-xs text-text-secondary">&gt;80% Good</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-warning" />
                <span className="text-xs text-text-secondary">60-80% Attention</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-danger" />
                <span className="text-xs text-text-secondary">&lt;60% Improve</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>{/* /data-guide-section="video-analysis" */}

      {/* ── 5. Diagnostic Stack — now the Drill-In Ledger (Slice 1e) ──────────────
          Five default-closed drawers: Goal checks · Phase breakdown · Movement
          economy (coach) · Visual & movement analysis · Technical diagnostics.
          Nothing deleted — every section from the old flat stack lives in a drawer.

          Speed & Tempo: kept outside the ledger (it uses CollapsibleSection with
          its own defaultExpanded logic that opens when off-target phases exist,
          which is a user-attention signal not suitable for a default-closed drawer). */}

      {/* Speed & Tempo — visible only when template opts in and ≥1 phase evaluated.
          Hidden in landscape focus mode. */}
      {(() => {
        const speedData = evaluation.metadata?.speed_assessment;
        if (!Array.isArray(speedData) || speedData.length === 0) return null;
        const hasSpeedData = speedData.some(p => p.verdict !== 'not_evaluated');
        if (!hasSpeedData) return null;
        const offTargetCount = speedData.filter(
          p => p.verdict === 'too_fast' || p.verdict === 'too_slow'
        ).length;
        return (
          <div className="max-lg:landscape:hidden">
            <CollapsibleSection
              title="Speed & Tempo"
              icon={ClockIcon}
              variant="default"
              badge={offTargetCount > 0 ? offTargetCount : undefined}
              defaultExpanded={offTargetCount > 0}
            >
              <SpeedTempoSection speedAssessment={speedData} />
            </CollapsibleSection>
          </div>
        );
      })()}

      {/* ── 6. AI Coaching Narrative (Slice 1d — editorial layout) ─────────
          PS-0a (score-fast split): show a loading skeleton while coaching is
          generating (isCoachingLoading=true), the full card once narrative
          arrives, or a subtle "unavailable" state if coaching timed out.
          When coaching is already present on first load (non-split evals),
          loading=false so there is no skeleton flash.
          Slice 1d: EvalEditorialCoaching replaces CoachingNarrativeCard's
          presentation with a magazine-style editorial layout. All data fields,
          timestamp chips, and role-gating are preserved.               */}
      {/* AI Coaching — hidden in landscape focus mode */}
      {showCoachingSection && (
        <div data-guide-section="coaching-narrative" className="max-lg:landscape:hidden">
        <CollapsibleSection
          title="AI Coaching"
          icon={SparklesIcon}
          variant="default"
          defaultExpanded={true}
        >
          <EvalEditorialCoaching
            narrative={evaluation.llm_explanation ?? null}
            deterministicObservations={evaluation.metadata?.deterministic_observations ?? null}
            geminiObservations={
              evaluation.metadata?.gemini_observations ??
              evaluation.llm_explanation?.gemini_observations ??
              null
            }
            scoreDecomposition={evaluation.llm_explanation?.score_decomposition ?? undefined}
            rawMatchScore={evaluation.metadata?.raw_match_score ?? null}
            gradeScaleApplied={evaluation.metadata?.grade_scale_applied ?? null}
            overallScore={evaluation.overall_score ?? null}
            onSeekToFramePct={handleSeekToFramePct}
            loading={isCoachingLoading}
            // 2026-09-21 report redesign (owner decision 2) RETIRES the
            // isCoach-derived gate here. The Score Breakdown's word-level
            // content is always shown; EvalEditorialCoaching itself keeps
            // the numeric detail (points/measured/expected) behind its own
            // "Score Details" toggle for every role.
            showScoreDecomposition={true}
            error={
              coachingTimedOut
                ? 'AI coaching is taking longer than expected. Your score is ready — check back shortly for the full coaching narrative.'
                : undefined
            }
          />
        </CollapsibleSection>
        </div>
      )}

      {/* ── Drill-In Ledger (Slice 1e) ──────────────────────────────────────────
          Five default-closed drawers below the editorial coaching section:
            1. Goal checks           (all users)
            2. Phase breakdown       (all users; FormScoreBreakdown inside is coach-only)
            3. Movement economy      (coach-only)
            4. Visual & movement     (all users; G-2 decision moves full version here)
            5. Technical diagnostics (all users, already default-closed per design §3)

          Movement Analysis (N4) is now inside Drawer 4 alongside Gemini observations.
          DebugDiagnosticsPanel now renders inside Drawer 5 / the ledger wrapper —
          nothing is deleted, everything is demoted.                              */}
      {/* EvalLedger — hidden in landscape focus mode */}
      <div className="max-lg:landscape:hidden">
      <EvalLedger
        isCoach={isCoach}
        spatialGoalResults={evaluation.spatial_goal_results ?? null}
        phaseScores={enrichedPhaseScores}
        legacyRegionScores={evaluation.region_scores}
        onPhaseClick={handlePhaseSeek}
        showPhaseScores={isCoach}
        showFormBreakdown={isCoach && showFormBreakdown}
        formBreakdownBadge={formBreakdownBadge}
        economyOfMotion={evaluation.metadata?.economy_of_motion ?? undefined}
        eomNarrative={evaluation.metadata?.eom_narrative ?? undefined}
        geminiObservations={evaluation.metadata?.gemini_observations ?? null}
        deterministicObservations={
          evaluation.metadata?.deterministic_observations ?? null
        }
        noLlmFallback={!hasNarrative && !isCoachingLoading}
        pricingResult={evaluation.metadata?.pricing_result ?? null}
      />
      </div>{/* /landscape:hidden EvalLedger wrapper */}

      {/* ── 7. Coach Comments + Share card — hidden in landscape focus mode ───── */}
      {evaluation && (
        <div className="max-lg:landscape:hidden">
        <>
          {/* ── B-2025-0019 + B-2026-0072: Share card ───────────────────── */}
          {/* Share modal (rendered here so it mounts/unmounts with the eval) */}
          {evaluation && (
            <ShareEvaluationModal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              evaluationId={evaluation.evaluation_id}
            />
          )}

          <div
            data-guide-section="share-card"
            style={{
              backgroundColor: 'var(--color-dark-card)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: '14px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <div>
              <p
                style={{
                  margin: '0 0 3px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                }}
              >
                Share your score
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  lineHeight: 1.5,
                }}
              >
                Create a shareable link or download a score card image.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Share link button — B-2025-0019 */}
              <button
                type="button"
                data-testid="share-link-button"
                onClick={() => setIsShareModalOpen(true)}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '9px 18px',
                  borderRadius: '999px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: 'var(--color-text-primary)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'border-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.35)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.18)';
                }}
              >
                <ShareIcon aria-hidden="true" style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                Share link
              </button>

              {/* Download card button — B-2026-0072 */}
              <button
                type="button"
                onClick={() => {
                  downloadScoreCard({
                    score: evaluation.overall_score ?? 0,
                    templateName: evaluation.template_name ?? evaluation.template_id ?? 'Evaluation',
                    channelName: evaluation.metadata?.channel_name as string | undefined,
                    athleteName: user?.display_name ?? undefined,
                  });
                }}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '9px 18px',
                  borderRadius: '999px',
                  backgroundColor: 'var(--color-accent)',
                  border: 'none',
                  color: 'var(--color-text-inverse)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'box-shadow 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 16px rgba(30,215,96,0.35)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
                }}
              >
                <svg
                  style={{ width: '14px', height: '14px', flexShrink: 0 }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download card
              </button>
            </div>
          </div>

          <div data-guide-section="comments">
          <CollapsibleSection
            title="Coach Comments"
            variant="default"
            defaultExpanded={false}
          >
            <CommentsSection
              evaluationId={evaluation.evaluation_id}
              templateVisibility="private"
              onSeekToTimestamp={(sec) => {
                // Convert absolute seconds to frame percentage and seek.
                const totalDuration = evaluation.metadata?.duration_seconds ?? 1;
                const pct = Math.min(1, sec / totalDuration);
                handleSeekToFramePct(pct);
              }}
            />
          </CollapsibleSection>
          </div>{/* /data-guide-section="comments" */}
        </>
        </div>
      )}
    </div>
    </EvalResultsPageShell>
  );
}

export default EvaluationResults;
